// NMJL 2026 Official Card — "Standard Hands and Rules", 89th Year.
// Source of truth: National Mah Jongg League, Inc. 2026 card. Transcribed for
// personal practice use only (see DECISIONS.md D-021). Hands © NMJL.
//
// This module is pure data + tiny constructors. It is consumed by
// `handMatch.js` (validation / AI targeting) and the Reference screen.
//
// ── Schema ────────────────────────────────────────────────────────────────
// A hand:
//   { id, section, label, desc, concealed, value, options: [ option, ... ] }
// `label`/`desc` are exactly as printed on the card (label = tiles, desc =
// the parenthetical). `-or-` lines and "These Nos Only" choices become
// multiple `options`; a hand matches if ANY option matches.
//
// An option:
//   { groups: [ group, ... ], vars?: { name: domain, ... } }
//
// A group is one tile-set the hand requires:
//   { n, num, suit }        suited number (num 1-9, or a var name string, or
//                            an expression like 's+1'); suit ∈ 'A'|'B'|'C'
//   { n, num: 0 }           White Dragon used as ZERO (no suit)
//   { n, dragon }           dragon ∈ 'red'|'green'|'white' | 'match:A' |
//                            'any' | a var name
//   { n, wind }             wind ∈ 'N'|'E'|'W'|'S' | a var name
//   { n, flower: true }     flowers
// `n` is the count: 1 single, 2 pair, 3 pung, 4 kong, 5 quint, 6 sextet.
// Jokers are allowed in any group with n >= 3 (pung/kong/quint/sextet) and
// never in singles or pairs — enforced by the matcher, not stored here.
//
// A var domain is one of:
//   { set: [numbers], ne?: [otherVarNames] }   number variable
//   { winds: true, ne?: [...] }                wind variable (N/E/W/S)
//   { dragons: true, ne?: [...] }              dragon variable (red/green/white)
//
// "match:A" resolves to the dragon matching suit A (Crak→Red, Bam→Green,
// Dot→White). "any" / "opp" dragons are modelled as "any dragon" — a lenient
// choice that is harmless for practice (see DECISIONS).

// ── tiny constructors ──────────────────────────────────────────────────────
const single = (num, suit) => ({ n: 1, num, suit });
const pair = (num, suit) => ({ n: 2, num, suit });
const pung = (num, suit) => ({ n: 3, num, suit });
const kong = (num, suit) => ({ n: 4, num, suit });
const quint = (num, suit) => ({ n: 5, num, suit });
const sextet = (num, suit) => ({ n: 6, num, suit });
const flowers = (n) => ({ n, flower: true });
const wind = (w, n = 1) => ({ n, wind: w });
const dragon = (d, n) => ({ n, dragon: d });
const zero = (n = 1) => ({ n, num: 0 }); // White Dragon as 0

const N1_9 = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const ODD = [1, 3, 5, 7, 9];
const EVEN = [2, 4, 6, 8];
const T369 = [3, 6, 9];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

// Pre-expand the two "pair X + the rest as singles + kongs of X" hands so the
// matcher stays generic (X over a small set → one fixed option each).
function pairSinglesKongs(set, extraFlowers) {
  return set.map((x) => {
    const groups = [];
    if (extraFlowers) groups.push(flowers(extraFlowers));
    groups.push(pair(x, 'A'));
    for (const o of set) if (o !== x) groups.push(single(o, 'A'));
    groups.push(kong(x, 'B'), kong(x, 'C'));
    return { groups };
  });
}

export const HANDS = [
  // ── 2026 ──────────────────────────────────────────────────────────────
  {
    id: '2026-1', section: '2026', label: '222 000 2222 6666',
    desc: 'Any 2 Suits', concealed: false, value: 25,
    options: [{ groups: [pung(2, 'A'), zero(3), kong(2, 'B'), kong(6, 'B')] }],
  },
  {
    id: '2026-2', section: '2026', label: '2026 DDD 2222 DDD',
    desc: 'Any 2 Suits w Matching Dragons, Kong 2 or 6', concealed: false, value: 25,
    options: [{
      groups: [
        single(2, 'A'), zero(1), single(2, 'A'), single(6, 'A'),
        dragon('match:A', 3), kong('k', 'B'), dragon('match:B', 3),
      ],
      vars: { k: { set: [2, 6] } },
    }],
  },
  {
    id: '2026-3', section: '2026', label: 'FFF 2026 222 6666',
    desc: 'Any 3 Suits', concealed: false, value: 25,
    options: [{
      groups: [
        flowers(3), single(2, 'A'), zero(1), single(2, 'A'), single(6, 'A'),
        pung(2, 'B'), kong(6, 'C'),
      ],
    }],
  },
  {
    id: '2026-4', section: '2026', label: '22 00 222 666 NEWS',
    desc: 'Any 2 Suits', concealed: false, value: 30,
    options: [{
      groups: [
        pair(2, 'A'), zero(2), pung(2, 'B'), pung(6, 'B'),
        wind('N'), wind('E'), wind('W'), wind('S'),
      ],
    }],
  },

  // ── 2468 ──────────────────────────────────────────────────────────────
  {
    id: '2468-1', section: '2468', label: '222 444 6666 8888',
    desc: 'Any 1 or 2 Suits', concealed: false, value: 25,
    options: [
      { groups: [pung(2, 'A'), pung(4, 'A'), kong(6, 'A'), kong(8, 'A')] },
      { groups: [pung(2, 'A'), pung(4, 'A'), kong(6, 'B'), kong(8, 'B')] },
    ],
  },
  {
    id: '2468-2', section: '2468', label: 'FF 2222 44 66 8888',
    desc: 'Any 2 Suits', concealed: false, value: 30,
    options: [{ groups: [flowers(2), kong(2, 'A'), pair(4, 'B'), pair(6, 'B'), kong(8, 'A')] }],
  },
  {
    id: '2468-3', section: '2468', label: 'EE 22 444 666 88 WW',
    desc: 'Any 1 Suit, East and West Only', concealed: false, value: 30,
    options: [{
      groups: [wind('E', 2), pair(2, 'A'), pung(4, 'A'), pung(6, 'A'), pair(8, 'A'), wind('W', 2)],
    }],
  },
  {
    id: '2468-4', section: '2468', label: '2222 DDD 8888 DDD',
    desc: 'Any 2 Suits w Matching Dragons, These Nos. Only', concealed: false, value: 25,
    options: [{
      groups: [kong(2, 'A'), dragon('match:A', 3), kong(8, 'B'), dragon('match:B', 3)],
    }],
  },
  {
    id: '2468-5', section: '2468', label: 'FFF 22 44 666 8888',
    desc: 'Any 1 Suit', concealed: false, value: 25,
    options: [{ groups: [flowers(3), pair(2, 'A'), pair(4, 'A'), pung(6, 'A'), kong(8, 'A')] }],
  },
  {
    id: '2468-6', section: '2468', label: '2468 2222 D 2222 D',
    desc: 'Any 3 Suits, Like Kongs 2,4,6 or 8 w Matching Dragon', concealed: false, value: 25,
    options: [{
      groups: [
        single(2, 'A'), single(4, 'A'), single(6, 'A'), single(8, 'A'),
        kong('k', 'B'), dragon('match:B', 1), kong('k', 'C'), dragon('match:C', 1),
      ],
      vars: { k: { set: EVEN } },
    }],
  },
  {
    id: '2468-7', section: '2468', label: 'FFF 2468 FFF 2222',
    desc: 'Any 2 Suits, Kong 2,4,6 or 8', concealed: false, value: 30,
    options: [{
      groups: [
        flowers(3), single(2, 'A'), single(4, 'A'), single(6, 'A'), single(8, 'A'),
        flowers(3), kong('k', 'B'),
      ],
      vars: { k: { set: EVEN } },
    }],
  },
  {
    id: '2468-8', section: '2468', label: 'FF 246 888 246 888',
    desc: 'Any 2 Suits', concealed: true, value: 30,
    options: [{
      groups: [
        flowers(2),
        single(2, 'A'), single(4, 'A'), single(6, 'A'), pung(8, 'A'),
        single(2, 'B'), single(4, 'B'), single(6, 'B'), pung(8, 'B'),
      ],
    }],
  },

  // ── ANY LIKE NUMBERS (k = any like number) ──────────────────────────────
  {
    id: 'ALN-1', section: 'Any Like Numbers', label: '1111 FFFFFF 1111',
    desc: 'Any 2 Suits', concealed: false, value: 30,
    options: [{
      groups: [kong('k', 'A'), flowers(6), kong('k', 'B')],
      vars: { k: { set: N1_9 } },
    }],
  },
  {
    id: 'ALN-2', section: 'Any Like Numbers', label: '1111 D 111 D 1111 D',
    desc: 'Any 3 Suits w Matching Dragon', concealed: false, value: 25,
    options: [{
      groups: [
        kong('k', 'A'), dragon('match:A', 1), pung('k', 'B'), dragon('match:B', 1),
        kong('k', 'C'), dragon('match:C', 1),
      ],
      vars: { k: { set: N1_9 } },
    }],
  },
  {
    id: 'ALN-3', section: 'Any Like Numbers', label: 'FF 1111 11 1111 DD',
    desc: 'Any 3 Suits w Any Dragon', concealed: false, value: 25,
    options: [{
      groups: [flowers(2), kong('k', 'A'), pair('k', 'B'), kong('k', 'C'), dragon('d', 2)],
      vars: { k: { set: N1_9 }, d: { dragons: true } },
    }],
  },

  // ── QUINTS ──────────────────────────────────────────────────────────────
  {
    id: 'Q-1', section: 'Quints', label: '11111 1111 11111',
    desc: 'Any 3 Suits, Any Like Nos.', concealed: false, value: 40,
    options: [{
      groups: [quint('k', 'A'), kong('k', 'B'), quint('k', 'C')],
      vars: { k: { set: N1_9 } },
    }],
  },
  {
    id: 'Q-2', section: 'Quints', label: 'FF 11111 22 33333',
    desc: 'Any 1 Suit, Any 3 Consec. Nos.', concealed: false, value: 45,
    options: [{
      groups: [flowers(2), quint('s', 'A'), pair('s+1', 'A'), quint('s+2', 'A')],
      vars: { s: { set: range(1, 7) } },
    }],
  },
  {
    id: 'Q-3', section: 'Quints', label: '11111 44444 DDDD',
    desc: 'Any 2 Nos. in Any 1 Suit w Opp. Dragon', concealed: false, value: 40,
    options: [{
      groups: [quint('a', 'A'), quint('b', 'A'), dragon('d', 4)],
      vars: { a: { set: N1_9 }, b: { set: N1_9, ne: ['a'] }, d: { dragons: true } },
    }],
  },

  // ── CONSECUTIVE RUN ─────────────────────────────────────────────────────
  {
    id: 'CR-1', section: 'Consecutive Run', label: '11 222 33 444 5555 -or- 55 666 77 888 9999',
    desc: 'Any 1 Suit, These Nos. Only', concealed: false, value: 25,
    options: [
      { groups: [pair(1, 'A'), pung(2, 'A'), pair(3, 'A'), pung(4, 'A'), kong(5, 'A')] },
      { groups: [pair(5, 'A'), pung(6, 'A'), pair(7, 'A'), pung(8, 'A'), kong(9, 'A')] },
    ],
  },
  {
    id: 'CR-2', section: 'Consecutive Run', label: 'FFF 1111 234 5555 -or- FFF 1111 234 5555',
    desc: 'Any 1 or 2 Suits, Any 5 Consec. Nos.', concealed: false, value: 25,
    options: [
      {
        groups: [flowers(3), kong('s', 'A'), single('s+1', 'A'), single('s+2', 'A'), single('s+3', 'A'), kong('s+4', 'A')],
        vars: { s: { set: range(1, 5) } },
      },
      {
        groups: [flowers(3), kong('s', 'A'), single('s+1', 'B'), single('s+2', 'B'), single('s+3', 'B'), kong('s+4', 'A')],
        vars: { s: { set: range(1, 5) } },
      },
    ],
  },
  {
    id: 'CR-3', section: 'Consecutive Run', label: '11 22 111 222 3333',
    desc: 'Any 3 Suits, Any 3 Consec. Nos.', concealed: false, value: 25,
    options: [{
      groups: [pair('s', 'A'), pair('s+1', 'A'), pung('s', 'B'), pung('s+1', 'B'), kong('s+2', 'C')],
      vars: { s: { set: range(1, 7) } },
    }],
  },
  {
    id: 'CR-4', section: 'Consecutive Run', label: '111 222 3333 4444 -or- 111 222 3333 4444',
    desc: 'Any 1 or 2 Suits, Any 4 Consec. Nos.', concealed: false, value: 25,
    options: [
      {
        groups: [pung('s', 'A'), pung('s+1', 'A'), kong('s+2', 'A'), kong('s+3', 'A')],
        vars: { s: { set: range(1, 6) } },
      },
      {
        groups: [pung('s', 'A'), pung('s+1', 'A'), kong('s+2', 'B'), kong('s+3', 'B')],
        vars: { s: { set: range(1, 6) } },
      },
    ],
  },
  {
    id: 'CR-5', section: 'Consecutive Run', label: 'FFF 11 22 333 DDDD -or- FFF 11 22 333 DDDD',
    desc: '1 or 2 Suits, Any Run, Dragons Match Middle No.', concealed: false, value: 25,
    options: [
      {
        groups: [flowers(3), pair('s', 'A'), pair('s+1', 'A'), pung('s+2', 'A'), dragon('match:A', 4)],
        vars: { s: { set: range(1, 7) } },
      },
      {
        groups: [flowers(3), pair('s', 'A'), pair('s+1', 'B'), pung('s+2', 'A'), dragon('match:B', 4)],
        vars: { s: { set: range(1, 7) } },
      },
    ],
  },
  {
    id: 'CR-6', section: 'Consecutive Run', label: '1111 FFFFFF 2222',
    desc: 'Any 1 Suit, Any 2 Consec. Nos.', concealed: false, value: 30,
    options: [{
      groups: [kong('s', 'A'), flowers(6), kong('s+1', 'A')],
      vars: { s: { set: range(1, 8) } },
    }],
  },
  {
    id: 'CR-7', section: 'Consecutive Run', label: 'FF 1111 2222 3333 -or- FF 1111 2222 3333',
    desc: 'Any 1 or 3 Suits, Any 3 Consec. Nos.', concealed: false, value: 25,
    options: [
      {
        groups: [flowers(2), kong('s', 'A'), kong('s+1', 'A'), kong('s+2', 'A')],
        vars: { s: { set: range(1, 7) } },
      },
      {
        groups: [flowers(2), kong('s', 'A'), kong('s+1', 'B'), kong('s+2', 'C')],
        vars: { s: { set: range(1, 7) } },
      },
    ],
  },
  {
    id: 'CR-8', section: 'Consecutive Run', label: '1 22 333 1 22 333 44',
    desc: 'Any 3 Suits, Any 4 Consec. Nos.', concealed: true, value: 35,
    options: [{
      groups: [
        single('s', 'A'), pair('s+1', 'A'), pung('s+2', 'A'),
        single('s', 'B'), pair('s+1', 'B'), pung('s+2', 'B'),
        pair('s+3', 'C'),
      ],
      vars: { s: { set: range(1, 6) } },
    }],
  },

  // ── 13579 ───────────────────────────────────────────────────────────────
  {
    id: 'O-1', section: '13579', label: '11 333 55 777 9999 -or- 11 333 55 777 9999',
    desc: 'Any 1 or 3 Suits', concealed: false, value: 25,
    options: [
      { groups: [pair(1, 'A'), pung(3, 'A'), pair(5, 'A'), pung(7, 'A'), kong(9, 'A')] },
      { groups: [pair(1, 'A'), pung(3, 'A'), pair(5, 'B'), pung(7, 'B'), kong(9, 'C')] },
    ],
  },
  {
    id: 'O-2', section: '13579', label: '111 333 3333 5555 -or- 555 777 7777 9999',
    desc: 'Any 2 Suits', concealed: false, value: 25,
    options: [
      { groups: [pung(1, 'A'), pung(3, 'A'), kong(3, 'B'), kong(5, 'B')] },
      { groups: [pung(5, 'A'), pung(7, 'A'), kong(7, 'B'), kong(9, 'B')] },
    ],
  },
  {
    id: 'O-3', section: '13579', label: 'NN 1111 33 5555 SS -or- NN 5555 77 9999 SS',
    desc: 'Any 1 Suit, North and South Only', concealed: false, value: 30,
    options: [
      { groups: [wind('N', 2), kong(1, 'A'), pair(3, 'A'), kong(5, 'A'), wind('S', 2)] },
      { groups: [wind('N', 2), kong(5, 'A'), pair(7, 'A'), kong(9, 'A'), wind('S', 2)] },
    ],
  },
  {
    id: 'O-4', section: '13579', label: '113579 1111 1111',
    desc: 'Any 3 Suits, Pair Any Odd No., Kongs Match Pair', concealed: false, value: 25,
    options: pairSinglesKongs(ODD, 0),
  },
  {
    id: 'O-5', section: '13579', label: 'FFF 11 33 555 DDDD -or- FFF 55 77 999 DDDD',
    desc: 'Any 1 Suit w Matching Dragon', concealed: false, value: 25,
    options: [
      { groups: [flowers(3), pair(1, 'A'), pair(3, 'A'), pung(5, 'A'), dragon('match:A', 4)] },
      { groups: [flowers(3), pair(5, 'A'), pair(7, 'A'), pung(9, 'A'), dragon('match:A', 4)] },
    ],
  },
  {
    id: 'O-6', section: '13579', label: '11 33 111 333 5555 -or- 55 77 555 777 9999',
    desc: 'Any 3 Suits', concealed: false, value: 25,
    options: [
      { groups: [pair(1, 'A'), pair(3, 'A'), pung(1, 'B'), pung(3, 'B'), kong(5, 'C')] },
      { groups: [pair(5, 'A'), pair(7, 'A'), pung(5, 'B'), pung(7, 'B'), kong(9, 'C')] },
    ],
  },
  {
    id: 'O-7', section: '13579', label: '1111 33 55 77 9999 -or- 1111 33 55 77 9999',
    desc: 'Any 1 or 2 Suits', concealed: false, value: 30,
    options: [
      { groups: [kong(1, 'A'), pair(3, 'A'), pair(5, 'A'), pair(7, 'A'), kong(9, 'A')] },
      { groups: [kong(1, 'A'), pair(3, 'B'), pair(5, 'B'), pair(7, 'B'), kong(9, 'A')] },
    ],
  },
  {
    id: 'O-8', section: '13579', label: 'FF 11 33 55 111 111 -or- FF 55 77 99 555 555',
    desc: 'Any 3 Suits, These Nos. Only', concealed: true, value: 35,
    options: [
      { groups: [flowers(2), pair(1, 'A'), pair(3, 'A'), pair(5, 'A'), pung(1, 'B'), pung(1, 'C')] },
      { groups: [flowers(2), pair(5, 'A'), pair(7, 'A'), pair(9, 'A'), pung(5, 'B'), pung(5, 'C')] },
    ],
  },
  {
    id: 'O-9', section: '13579', label: 'FF 135 777 999 DDD',
    desc: 'Any 1 Suit w Opp. Dragon', concealed: true, value: 30,
    options: [{
      groups: [flowers(2), single(1, 'A'), single(3, 'A'), single(5, 'A'), pung(7, 'A'), pung(9, 'A'), dragon('d', 3)],
      vars: { d: { dragons: true } },
    }],
  },

  // ── WINDS-DRAGONS ───────────────────────────────────────────────────────
  {
    id: 'WD-1', section: 'Winds-Dragons', label: 'NNNN EEE WWW SSSS -or- NNN EEEE WWWW SSS',
    desc: '', concealed: false, value: 25,
    options: [
      { groups: [wind('N', 4), wind('E', 3), wind('W', 3), wind('S', 4)] },
      { groups: [wind('N', 3), wind('E', 4), wind('W', 4), wind('S', 3)] },
    ],
  },
  {
    id: 'WD-2', section: 'Winds-Dragons', label: '1234 DDD DDD DDDD',
    desc: 'Any 4 Consec. Nos. in Any 1 Suit, Any 3 Dragons', concealed: false, value: 25,
    options: [{
      groups: [
        single('s', 'A'), single('s+1', 'A'), single('s+2', 'A'), single('s+3', 'A'),
        dragon('d1', 3), dragon('d2', 3), dragon('d3', 4),
      ],
      vars: {
        s: { set: range(1, 6) },
        d1: { dragons: true }, d2: { dragons: true, ne: ['d1'] }, d3: { dragons: true, ne: ['d1', 'd2'] },
      },
    }],
  },
  {
    id: 'WD-3', section: 'Winds-Dragons', label: 'NNN 1111 1111 SSS',
    desc: 'Any Like Odd Nos. in Any 2 Suits', concealed: false, value: 25,
    options: [{
      groups: [wind('N', 3), kong('k', 'A'), kong('k', 'B'), wind('S', 3)],
      vars: { k: { set: ODD } },
    }],
  },
  {
    id: 'WD-4', section: 'Winds-Dragons', label: 'EEE 2222 2222 WWW',
    desc: 'Any Like Even Nos. in Any 2 Suits', concealed: false, value: 25,
    options: [{
      groups: [wind('E', 3), kong('k', 'A'), kong('k', 'B'), wind('W', 3)],
      vars: { k: { set: EVEN } },
    }],
  },
  {
    id: 'WD-5', section: 'Winds-Dragons', label: 'FFF NNNN FFF DDDD',
    desc: 'Any Wind, Any Dragon', concealed: false, value: 25,
    options: [{
      groups: [flowers(3), wind('w', 4), flowers(3), dragon('d', 4)],
      vars: { w: { winds: true }, d: { dragons: true } },
    }],
  },
  {
    id: 'WD-6', section: 'Winds-Dragons', label: '1 N 2 EE 3 WWW 4 SSSS',
    desc: 'Any 1 Suit, These Nos. Only', concealed: false, value: 25,
    options: [{
      groups: [
        single(1, 'A'), wind('N', 1), single(2, 'A'), wind('E', 2),
        single(3, 'A'), wind('W', 3), single(4, 'A'), wind('S', 4),
      ],
    }],
  },
  {
    id: 'WD-7', section: 'Winds-Dragons', label: 'FF NNNN SSSS DD DD -or- FF EEEE WWWW DD DD',
    desc: 'Any 2 Dragons', concealed: false, value: 25,
    options: [
      {
        groups: [flowers(2), wind('N', 4), wind('S', 4), dragon('d1', 2), dragon('d2', 2)],
        vars: { d1: { dragons: true }, d2: { dragons: true, ne: ['d1'] } },
      },
      {
        groups: [flowers(2), wind('E', 4), wind('W', 4), dragon('d1', 2), dragon('d2', 2)],
        vars: { d1: { dragons: true }, d2: { dragons: true, ne: ['d1'] } },
      },
    ],
  },
  {
    id: 'WD-8', section: 'Winds-Dragons', label: 'NN EEE 2026 WWW SS',
    desc: '2026 Any 1 Suit', concealed: true, value: 30,
    options: [{
      groups: [
        wind('N', 2), wind('E', 3),
        single(2, 'A'), zero(1), single(2, 'A'), single(6, 'A'),
        wind('W', 3), wind('S', 2),
      ],
    }],
  },

  // ── 369 ─────────────────────────────────────────────────────────────────
  {
    id: '369-1', section: '369', label: '333 666 6666 9999 -or- 333 666 6666 9999',
    desc: 'Any 2 or 3 Suits', concealed: false, value: 25,
    options: [
      { groups: [pung(3, 'A'), pung(6, 'A'), kong(6, 'B'), kong(9, 'B')] },
      { groups: [pung(3, 'A'), pung(6, 'B'), kong(6, 'C'), kong(9, 'A')] },
    ],
  },
  {
    id: '369-2', section: '369', label: '33 66 333 666 9999',
    desc: 'Any 3 Suits', concealed: false, value: 25,
    options: [{ groups: [pair(3, 'A'), pair(6, 'A'), pung(3, 'B'), pung(6, 'B'), kong(9, 'C')] }],
  },
  {
    id: '369-3', section: '369', label: 'FFF 33 666 99 DDDD -or- FFF 33 666 99 DDDD',
    desc: '1 Suit w Matching or Opp. Dragon', concealed: false, value: 25,
    options: [{
      groups: [flowers(3), pair(3, 'A'), pung(6, 'A'), pair(9, 'A'), dragon('d', 4)],
      vars: { d: { dragons: true } },
    }],
  },
  {
    id: '369-4', section: '369', label: '33 66 666 999 NEWS',
    desc: 'Any 2 Suits', concealed: false, value: 30,
    options: [{
      groups: [
        pair(3, 'A'), pair(6, 'A'), pung(6, 'B'), pung(9, 'B'),
        wind('N'), wind('E'), wind('W'), wind('S'),
      ],
    }],
  },
  {
    id: '369-5', section: '369', label: 'FF 3369 3333 3333',
    desc: 'Any 3 Suits, Pair 3, 6 or 9, Kongs Match Pair', concealed: false, value: 25,
    options: pairSinglesKongs(T369, 2),
  },
  {
    id: '369-6', section: '369', label: 'FF 333 666 999 369',
    desc: 'Any 2 Suits', concealed: true, value: 30,
    options: [{
      groups: [
        flowers(2), pung(3, 'A'), pung(6, 'A'), pung(9, 'A'),
        single(3, 'B'), single(6, 'B'), single(9, 'B'),
      ],
    }],
  },

  // ── SINGLES AND PAIRS (all concealed) ───────────────────────────────────
  {
    id: 'SP-1', section: 'Singles and Pairs', label: 'NN EE WW SS 1D 1D 1D',
    desc: 'Any 3 Suits, Any Like No. w Matching Dragon', concealed: true, value: 50,
    options: [{
      groups: [
        wind('N', 2), wind('E', 2), wind('W', 2), wind('S', 2),
        single('k', 'A'), dragon('match:A', 1),
        single('k', 'B'), dragon('match:B', 1),
        single('k', 'C'), dragon('match:C', 1),
      ],
      vars: { k: { set: N1_9 } },
    }],
  },
  {
    id: 'SP-2', section: 'Singles and Pairs', label: '2 4 66 88 2 4 66 88 88',
    desc: 'Any 3 Suits, These Nos. Only', concealed: true, value: 50,
    options: [{
      groups: [
        single(2, 'A'), single(4, 'A'), pair(6, 'A'), pair(8, 'A'),
        single(2, 'B'), single(4, 'B'), pair(6, 'B'), pair(8, 'B'),
        pair(8, 'C'),
      ],
    }],
  },
  {
    id: 'SP-3', section: 'Singles and Pairs', label: 'FF 3369 3669 3699',
    desc: 'Any 3 Suits', concealed: true, value: 50,
    options: [{
      groups: [
        flowers(2),
        pair(3, 'A'), single(6, 'A'), single(9, 'A'),
        single(3, 'B'), pair(6, 'B'), single(9, 'B'),
        single(3, 'C'), single(6, 'C'), pair(9, 'C'),
      ],
    }],
  },
  {
    id: 'SP-4', section: 'Singles and Pairs', label: '11 22 33 44 55 66 77',
    desc: 'Any 1 Suit, Any 7 Consec. Nos.', concealed: true, value: 50,
    options: [{
      groups: [
        pair('s', 'A'), pair('s+1', 'A'), pair('s+2', 'A'), pair('s+3', 'A'),
        pair('s+4', 'A'), pair('s+5', 'A'), pair('s+6', 'A'),
      ],
      vars: { s: { set: range(1, 3) } },
    }],
  },
  {
    id: 'SP-5', section: 'Singles and Pairs', label: '11 357 99 11 357 99',
    desc: 'Any 2 Suits', concealed: true, value: 50,
    options: [{
      groups: [
        pair(1, 'A'), single(3, 'A'), single(5, 'A'), single(7, 'A'), pair(9, 'A'),
        pair(1, 'B'), single(3, 'B'), single(5, 'B'), single(7, 'B'), pair(9, 'B'),
      ],
    }],
  },
  {
    id: 'SP-6', section: 'Singles and Pairs', label: 'FF 2026 2026 2026',
    desc: 'Any 3 Suits', concealed: true, value: 75,
    options: [{
      groups: [
        flowers(2),
        single(2, 'A'), zero(1), single(2, 'A'), single(6, 'A'),
        single(2, 'B'), zero(1), single(2, 'B'), single(6, 'B'),
        single(2, 'C'), zero(1), single(2, 'C'), single(6, 'C'),
      ],
    }],
  },
];

export const SECTIONS = [
  '2026', '2468', 'Any Like Numbers', 'Quints', 'Consecutive Run',
  '13579', 'Winds-Dragons', '369', 'Singles and Pairs',
];

export const CARD_CREDIT =
  'Hands © National Mah Jongg League — 2026 Card (89th Year). For personal practice use.';
